import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { orgRosterResponseSchema, unitsResponseSchema } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { ApiError } from '../../components/ApiError.js';
import { useClaimRoles } from '../auth/useClaimRoles.js';
import { CreateSoldierForm } from '../users/CreateSoldierForm.js';
import { OrgGrid } from './OrgGrid.js';
import { totals } from './orgRosterShape.js';
import { PLATOON_LEADER_ROLES, STOREROOM_ROLES } from '../../components/RequireRole.js';

/**
 * The org page (REQUIREMENTS §2.6; owner 2026-09-13: one page, and "the
 * table is already the org structure"). Four tiles, then the grid: every
 * soldier in the caller's reach grouped platoon → team, each unit's row
 * carrying its counts and actions — the structure, the counts and the
 * editor in one place. `/org/:unitId` (the plain roster) and the soldier
 * page are unchanged.
 */
export function OrgPage() {
  const { t } = useTranslation();
  const units = useApi('/units', unitsResponseSchema);
  const roster = useApi('/units/roster', orgRosterResponseSchema);
  const { roles } = useClaimRoles();
  const isAdmin = roles.includes('admin');
  const storeroom = roles.some((r) => STOREROOM_ROLES.includes(r));
  const canBulk = storeroom || roles.some((r) => PLATOON_LEADER_ROLES.includes(r));

  const rows = roster.kind === 'ready' ? roster.data.rows : [];
  const sum = useMemo(() => totals(rows), [rows]);

  if (units.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (units.kind === 'error') return <ApiError code={units.code} requestId={units.requestId} />;

  const reload = () => {
    units.reload();
    roster.reload();
  };

  return (
    <>
      <section className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="org-tiles">
        <Tile label={t('org.tiles.soldiers')} value={sum.soldiers} testId="org-tile-soldiers" />
        <Tile label={t('org.tiles.openGear')} value={sum.openGear} testId="org-tile-open" />
        <Tile label={t('org.tiles.unsigned')} value={sum.unsignedForms} testId="org-tile-unsigned" to="/forms?status=pending_signature" />
        <Tile label={t('org.tiles.overdue')} value={sum.overdue} testId="org-tile-overdue" to="/alerts" warn />
      </section>

      <section className="min-w-0 rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900" data-testid="org-grid-title">
            {t('org.title')}
          </h2>
          {isAdmin && <CreateSoldierForm onCreated={reload} />}
        </div>
        <div className="mt-3">
          {roster.kind === 'loading' && <p className="text-slate-500">{t('home.loading')}</p>}
          {roster.kind === 'error' && <ApiError code={roster.code} requestId={roster.requestId} />}
          {roster.kind === 'ready' && (
            <OrgGrid
              rows={rows}
              units={units.data.units}
              viewableUnitIds={units.data.viewableUnitIds}
              admin={isAdmin}
              canIssue={canBulk}
              storeroom={storeroom}
              onChanged={reload}
            />
          )}
        </div>
      </section>
    </>
  );
}

function Tile({ label, value, testId, to, warn }: { label: string; value: number; testId: string; to?: string; warn?: boolean }) {
  const body = (
    <>
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`latin-inline text-2xl font-semibold ${warn && value > 0 ? 'text-red-700' : 'text-slate-900'}`}>{value}</p>
    </>
  );
  const cls = 'block rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200';
  return to ? (
    <Link to={to} className={`${cls} hover:bg-slate-50`} data-testid={testId}>
      {body}
    </Link>
  ) : (
    <div className={cls} data-testid={testId}>
      {body}
    </div>
  );
}
